import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { signupApi, tierApi } from '@/services/api';
import { toast } from 'react-hot-toast';
import { debug } from '@/utils/debug';
import { Logo } from '@/components/Logo';
import { LoadingSpinner, DynamicBanner } from '@/components/ui';
import { TierStatusBanner } from '@/components/features/TierStatusBanner';
import { TIER_CONFIG } from '@/config/constants';
import type { OnboardingData, OnboardingStatusResponse } from '@/types/signup';
import type { TierStatus } from '@/services/api/tier';

export function Onboarding() {
  const navigate = useNavigate();
  const [onboardingData, setOnboardingData] = useState<OnboardingData | null>(null);

  // Load onboarding data from session storage
  useEffect(() => {
    const sessionData = sessionStorage.getItem('onboarding_data');
    if (!sessionData) {
      toast.error('Onboarding session not found. Please sign up again.');
      navigate('/signup');
      return;
    }

    const data = JSON.parse(sessionData) as OnboardingData;
    setOnboardingData(data);
    debug.log('Onboarding data loaded:', data);
  }, [navigate]);

  // Fallback Google Ads signup conversion fire. The PRIMARY fire is in
  // Signup/index.tsx's `onVerified` callback — that's the stable signal for
  // "signup just completed". This one only fires if (a) the user somehow
  // reaches /onboarding?verified=true without the Signup callback running
  // (e.g., closed the signup tab, opened a bookmark), AND (b) the
  // sessionStorage guard hasn't already been set.
  //
  // Caveat: /onboarding redirects to /signup on missing session data, which
  // races this useEffect — so don't rely on this path. It's defensive depth,
  // not the primary mechanism.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('verified') !== 'true') return;
    if (sessionStorage.getItem('signup_conversion_fired') === '1') return;
    const sendTo = import.meta.env.VITE_SIGNUP_CONVERSION_SEND_TO;
    if (!sendTo) {
      debug.warn('Onboarding: VITE_SIGNUP_CONVERSION_SEND_TO not set; skipping conversion fire');
      return;
    }
    if (typeof window.gtag !== 'function') {
      debug.warn('Onboarding: verified=true reached but gtag is not loaded; skipping');
      return;
    }
    window.gtag('event', 'conversion', { send_to: sendTo });
    sessionStorage.setItem('signup_conversion_fired', '1');
    debug.log('Onboarding: Google Ads signup conversion fired (fallback path)');
  }, []);

  // Fallback Meta Pixel CompleteRegistration fire. Mirrors the gtag
  // fallback above for the same edge case (user reaches
  // /onboarding?verified=true without the Signup callback running).
  // Independent sessionStorage guard from Google's so one platform's
  // success doesn't suppress the other.
  //
  // eventID reuses the random UUID the Signup primary fire stashed in
  // sessionStorage (so a primary fire that landed in this tab dedupes
  // cleanly against the fallback). If the primary fire happened in a
  // different tab (sessionStorage is per-tab) we mint a fresh random
  // UUID — still non-identifying, just unable to dedupe against the
  // primary. We deliberately do not derive from member_uuid so Meta
  // never sees Flouri's stable internal user identifier.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('verified') !== 'true') return;
    if (sessionStorage.getItem('meta_signup_conversion_fired') === '1') return;
    if (typeof window.fbq !== 'function') {
      debug.warn('Onboarding: verified=true reached but fbq is not loaded; skipping Meta fire');
      return;
    }
    let metaEventID = sessionStorage.getItem('meta_signup_event_id');
    if (!metaEventID) {
      metaEventID = crypto.randomUUID();
      sessionStorage.setItem('meta_signup_event_id', metaEventID);
    }
    window.fbq(
      'track',
      'CompleteRegistration',
      { value: 0, currency: 'USD' },
      { eventID: metaEventID },
    );
    sessionStorage.setItem('meta_signup_conversion_fired', '1');
    debug.log('Onboarding: Meta CompleteRegistration conversion fired (fallback path)');
  }, []);

  // Fetch onboarding status
  const { data: status, isLoading: isLoadingStatus } = useQuery<OnboardingStatusResponse>({
    queryKey: ['onboarding-status', onboardingData?.member_uuid],
    queryFn: async () => {
      const result = await signupApi.getOnboardingStatus(onboardingData!.member_uuid);
      debug.log('Onboarding status fetched:', result);
      return result;
    },
    enabled: !!onboardingData?.member_uuid,
    refetchInterval: false,
    refetchOnMount: 'always',
    staleTime: 0,
  });

  // Fetch tier status
  const { data: tierStatus, isLoading: isLoadingTier } = useQuery<TierStatus>({
    queryKey: ['tier-status'],
    queryFn: async () => {
      const result = await tierApi.getStatus();
      debug.log('Tier status fetched:', result);
      return result;
    },
    enabled: !!onboardingData?.member_uuid,
    refetchInterval: false,
    staleTime: 0,
  });

  // Complete onboarding mutation
  const completeOnboardingMutation = useMutation({
    mutationFn: () => signupApi.completeOnboarding(onboardingData!.member_uuid),
    onSuccess: (response) => {
      debug.log('Onboarding completed successfully:', response);
      // Store auth tokens
      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('refresh_token', response.refresh_token);
      // Clear onboarding session data
      sessionStorage.removeItem('onboarding_data');
      toast.success('Welcome! Your account is ready.');
      // Navigate to dashboard
      navigate('/dashboard');
    },
    onError: (error: unknown) => {
      debug.error('Complete onboarding failed:', error);
      const apiError = error as { response?: { data?: { detail?: string } } };
      toast.error(apiError.response?.data?.detail || 'Failed to complete onboarding');
    },
  });

  // For free tier, onboarding is considered complete when account is created
  const isComplete = status?.steps.signup === 'completed';

  const isLoading = isLoadingStatus || isLoadingTier;

  if (isLoading || !onboardingData || !status) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="xl" variant="primary" className="mx-auto" label="Loading onboarding" />
          <p className="mt-4 text-gray-600">Loading your onboarding...</p>
        </div>
      </div>
    );
  }

  // Get tier type label
  const tierTypeLabel = tierStatus?.tier_type === 'seasonal' ? 'Seasonal' : 'Standard';

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <style>{`
        @keyframes progressGradientShift {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }
      `}</style>
      <div className="max-w-3xl mx-auto">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Logo variant="color" size="lg" className="!h-[64px] ml-6" />
          </div>
          <p className="text-gray-600">Let's get to flourishing.</p>
        </div>

        {/* Dynamic Banner */}
        <DynamicBanner className="mb-8">
          {isComplete
            ? "You're all set! Your account is ready."
            : "Almost there! Complete your setup below."}
        </DynamicBanner>

        {/* Main Container */}
        <div className="bg-white rounded-lg shadow-md p-8">
          {/* Progress Bar */}
          <div className="w-full h-2 bg-gray-200 rounded-full mb-8 overflow-hidden">
            <div
              className="h-full transition-all duration-300"
              style={{
                width: isComplete ? '100%' : '50%',
                background: 'linear-gradient(90deg, #373636 0%, #ee3962 50%, #373636 100%)',
                backgroundSize: '200% 100%',
                animation: 'progressGradientShift 12s ease-in-out infinite',
              }}
            />
          </div>

          {/* Step Indicator */}
          <div className="flex justify-between mb-12">
            {/* Account Created Step */}
            <div className="flex flex-col items-center flex-1">
              <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center font-bold mb-2">
                ✓
              </div>
              <div className="text-sm text-center text-gray-700 font-medium">Account Created</div>
            </div>

            {/* Link Google Ads Step */}
            <div className="flex flex-col items-center flex-1">
              <div className="w-8 h-8 rounded-full bg-blue-700 text-white flex items-center justify-center font-bold mb-2">
                2
              </div>
              <div className="text-sm text-center text-gray-700 font-medium">Link Google Ads</div>
            </div>
          </div>

          {/* Welcome Message */}
          <div className="mb-8 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Welcome!
            </h2>
            <p className="text-gray-600">
              Your account is ready. Click below to access your dashboard and link your Google Ads account.
            </p>
          </div>

          {/* Tier Information */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Your {tierTypeLabel} Plan</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5">
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-gray-700">Full platform access - completely free</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5">
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-gray-700">AI-powered campaign recommendations</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5">
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-gray-700">Automatic budget allocation across campaigns</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5">
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-gray-700">Bid optimization</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5">
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-gray-700">Maintain full control of your campaigns!</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5">
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-gray-700">
                  {onboardingData?.tier_type === 'seasonal'
                    ? `Requirement: Spend $${TIER_CONFIG.SEASONAL_MIN_SPEND_PER_ACCOUNT_YEAR} in 4 months, $${TIER_CONFIG.SEASONAL_MIN_SPEND_PER_ACCOUNT_YEAR}/year onward per linked Google Ads account`
                    : `Requirement: Spend $${TIER_CONFIG.STANDARD_MIN_SPEND_PER_ACCOUNT_30DAYS}/month per linked Google Ads account after 1 month`}
                </span>
              </div>
            </div>
          </div>

          {/* Tier Status Banner */}
          {tierStatus && (
            <div className="mb-8">
              <TierStatusBanner tierStatus={tierStatus} compact />
            </div>
          )}

          {/* Status List */}
          <div className="space-y-4 mb-8">
            {/* Account Creation */}
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0">
                ✓
              </div>
              <div>
                <div className="font-semibold text-gray-900">Account Creation</div>
                <div className="text-sm text-gray-600">Your account has been created successfully</div>
              </div>
            </div>

            {/* Link Google Ads */}
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center flex-shrink-0">
                ○
              </div>
              <div>
                <div className="font-semibold text-gray-900">Link Google Ads</div>
                <div className="text-sm text-gray-600">
                  Connect your Google Ads account from the dashboard to start optimizing
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 justify-end">
            <button
              onClick={() => completeOnboardingMutation.mutate()}
              disabled={completeOnboardingMutation.isPending}
              className="px-6 py-3 bg-blue-700 text-white rounded-md hover:bg-blue-800 font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {completeOnboardingMutation.isPending ? 'Completing...' : 'Go to Dashboard'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
